import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { updateProfile } from 'firebase/auth';
import Products2Hero from '../components/Products2Hero';
import { 
  ShoppingBag, 
  CreditCard, 
  UserPlus, 
  Calendar, 
  Package, 
  ChevronRight,
  ShieldCheck,
  Star
} from 'lucide-react';
import './ProfilePage.css';

const ProfilePage = () => {
  const { currentUser, logout, getUserData, updateUserData } = useAuth();
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    profileImageUrl: ''
  });
  const [userOrders, setUserOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadUserData();
    if (currentUser) {
      fetchUserOrders();
    }
  }, [currentUser]);

  const loadUserData = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const data = await getUserData(currentUser.uid);
      if (data) {
        setUserData({
          name: data.name || currentUser.displayName || '',
          email: data.email || currentUser.email || '',
          phoneNumber: data.phoneNumber || currentUser.phoneNumber || '',
          profileImageUrl: data.profileImageUrl || currentUser.photoURL || ''
        });
      } else {
        // Fallback to Firebase Auth data
        setUserData({
          name: currentUser.displayName || '',
          email: currentUser.email || '',
          phoneNumber: currentUser.phoneNumber || '',
          profileImageUrl: currentUser.photoURL || ''
        });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    if (!userData.name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Update Firebase Auth profile
      await updateProfile(currentUser, {
        displayName: userData.name,
        photoURL: userData.profileImageUrl
      });

      // Update Firestore user data
      await updateUserData(currentUser.uid, {
        name: userData.name,
        email: userData.email,
        phoneNumber: userData.phoneNumber,
        profileImageUrl: userData.profileImageUrl,
        updatedAt: Date.now()
      });

      setSuccess('Profile updated successfully!');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError('');
    setSuccess('');
    loadUserData(); // Reset to original data
  };

  const fetchUserOrders = async () => {
    if (!currentUser?.uid) {
      setOrdersLoading(false);
      return;
    }
    setOrdersLoading(true);
    setOrdersError('');
    try {
      const { queryDocuments } = await import('../services/firebaseService');
      const orders = await queryDocuments('orders', [{ field: 'userId', operator: '==', value: currentUser.uid }], { field: 'createdAt', direction: 'desc' });
      setUserOrders(orders);
    } catch (err) {
      console.error('Error fetching user orders:', err);
      setOrdersError('Failed to load your orders.');
    } finally {
      setOrdersLoading(false);
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          <div className="loading-spinner"></div>
          <p>Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Hero Section */}
      <Products2Hero 
        tagline="ACCOUNT DASHBOARD · PERSONAL PROFILE · PREFERENCES"
        title1="Account"
        title2="Overview"
        title3="Hub."
        description="Manage your professional profile, track your engagement metrics, and customize your experience all in one place."
        button1Text="Shop Collection"
        button1Link="/laptops"
        button2Text="Logout"
        button2Link="#logout"
        button2OnClick={logout}
      />

      <div className="container">
        <div className="profile-layout">
          {/* Profile Card */}
          <div className="profile-card">
            <div className="profile-header">
              <div className="profile-avatar-section">
                <div className="relative">
                  <div className="profile-avatar">
                    {userData.profileImageUrl ? (
                      <img src={userData.profileImageUrl} alt="Profile" />
                    ) : (
                      <span className="avatar-initials">
                        {getInitials(userData.name || 'User')}
                      </span>
                    )}
                  </div>
                  <div className="absolute -bottom-1 -right-1 bg-[#0063B8] text-white p-1 rounded-full border-2 border-white shadow-sm">
                    <ShieldCheck size={14} />
                  </div>
                </div>
                <div className="profile-info">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-2xl font-black tracking-tight">{userData.name || 'User'}</h2>
                    <span className="px-2 py-0.5 rounded-full bg-[#0063B8]/10 text-[#0063B8] text-[10px] font-bold uppercase tracking-widest border border-[#0063B8]/20">Verified User</span>
                  </div>
                  <p className="profile-email text-sm text-gray-500 font-medium">{userData.email}</p>
                </div>
              </div>
              
              {!isEditing && (
                <button 
                  className="edit-profile-action-btn"
                  onClick={() => setIsEditing(true)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Edit Profile
                </button>
              )}
            </div>

            {error && (
              <div className="error-message">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                {error}
              </div>
            )}

            {success && (
              <div className="success-message">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                </svg>
                {success}
              </div>
            )}

            <div className="profile-form">
              <div className="form-section">
                <h3>Personal Information</h3>
                
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="name">Full Name</label>
                    <div className="input-wrapper">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={userData.name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Enter your full name"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <div className="input-wrapper">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                      </svg>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={userData.email}
                        onChange={handleInputChange}
                        disabled={true} // Email usually shouldn't be editable
                        placeholder="Enter your email"
                      />
                    </div>
                    <small className="input-hint">Email cannot be changed for security reasons</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="phoneNumber">Phone Number</label>
                    <div className="input-wrapper">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                      </svg>
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={userData.phoneNumber}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {isEditing && (
                <div className="form-actions">
                  <button 
                    className="btn-secondary"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn-primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <div className="loading-spinner-small"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                        </svg>
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Account Overview Stats Redesign */}
          <div id="activity" className="account-overview-section">
            <div className="section-head mb-12">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#0063B8]/30 bg-[#0063B8]/10 text-[#0063B8] text-[10px] font-bold uppercase tracking-widest mb-4">
                ACCOUNT ACTIVITY
              </span>
              <h3 className="text-3xl font-black tracking-tight text-[#0D1F17]">Your Insights <span className="text-[#0063B8]">& Stats.</span></h3>
              <p className="text-gray-500 font-medium text-sm mt-2">Comprehensive breakdown of your engagement with Nexawave.</p>
            </div>
            
            <div className="stats-modern-grid">
              {[
                { 
                  icon: Package, 
                  label: 'Placed Orders', 
                  value: ordersLoading ? '...' : userOrders.length, 
                  desc: 'Total successfully confirmed orders',
                  color: '#0063B8'
                },
                { 
                  icon: CreditCard, 
                  label: 'Total Value', 
                  value: ordersLoading ? '...' : `₹${userOrders.reduce((acc, order) => acc + order.totalAmount, 0).toLocaleString()}`, 
                  desc: 'Cumulative lifetime procurement spend',
                  color: '#0063B8'
                },
                { 
                  icon: Calendar, 
                  label: 'Member Tenure', 
                  value: new Date(currentUser?.metadata?.creationTime).getFullYear(), 
                  desc: `Active since ${new Date(currentUser?.metadata?.creationTime).toLocaleDateString()}`,
                  color: '#0063B8'
                },
                { 
                  icon: Star, 
                  label: 'Tier Status', 
                  value: 'Elite User ', 
                  desc: 'Unlocked exclusive wholesale pricing tier',
                  color: '#0063B8'
                }
              ].map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <div key={i} className="stat-premium-card group">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-[#0063B8]/5 rounded-bl-[60px] transition-all group-hover:bg-[#0063B8]/10 group-hover:scale-110"></div>
                    <div className="stat-premium-icon">
                      <Icon size={20} strokeWidth={2.5} />
                    </div>
                    <div className="stat-label-small">{stat.label}</div>
                    <div className="stat-value-large mb-2">{stat.value}</div>
                    <p className="text-[11px] text-gray-400 font-medium leading-tight">{stat.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Recent Orders section removed per user request */}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;